/**
 * KEYBOARD SHORTCUTS.
 *
 *   /      focus the search box
 *   f      toggle the filter rail / sheet
 *   e      export
 *   s      save the current view
 *   1-5    switch tab
 *   Esc    clear the drill selection; if there is none, close the sheet
 *   ?      show the list
 *
 * ── EVERY ONE OF THEM IS SKIPPED WHILE SOMEBODY IS TYPING ──
 *
 * A screen with single-letter shortcuts and a search box is a screen where
 * typing "france" jumps three tabs and opens an export. The guard checks the
 * event's TARGET rather than a focus flag, because the target is the thing that
 * is actually receiving the character.
 *
 * They are also skipped while a modal is open, so a dialog keeps Escape for
 * itself — a shortcut that steals Escape from a record editor is a shortcut that
 * loses somebody's edit.
 */
import { useEffect } from 'react';
import { TABS, Tab } from '../state/filter-model';

const TYPING_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT']);

const isTyping = (target: EventTarget | null): boolean => {
  const el = target as HTMLElement | null;
  if (!el) return false;
  if (TYPING_TAGS.has(el.tagName)) return true;

  return el.isContentEditable === true;
};

const modalIsOpen = (): boolean =>
  typeof document !== 'undefined' &&
  document.querySelector('[role="dialog"][aria-modal="true"]') !== null;

export interface ShortcutHandlers {
  onTab: (tab: Tab) => void;
  onFilters: () => void;
  onExport: () => void;
  onSearch?: () => void;
  onEscape: () => void;
  onHelp?: () => void;
  /** Tabs the reader cannot reach, so `2` does not land on a hidden one. */
  hidden?: Tab[];
}

export const useKeyboardShortcuts = ({
  onTab,
  onFilters,
  onExport,
  onSearch,
  onEscape,
  onHelp,
  hidden = [],
}: ShortcutHandlers): void => {
  useEffect(() => {
    const visible = TABS.filter(tab => !hidden.includes(tab));

    const handler = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTyping(event.target)) return;

      // Escape is the one key a dialog owns outright.
      if (modalIsOpen() && event.key !== 'Escape') return;
      if (modalIsOpen() && event.key === 'Escape') return;

      switch (event.key) {
        case '/':
          event.preventDefault();
          onSearch?.();
          break;
        case 'f':
          event.preventDefault();
          onFilters();
          break;
        case 'e':
          event.preventDefault();
          onExport();
          break;
        case '?':
          event.preventDefault();
          onHelp?.();
          break;
        case 'Escape':
          onEscape();
          break;
        default: {
          const index = Number(event.key);
          if (Number.isInteger(index) && index >= 1 && index <= visible.length) {
            event.preventDefault();
            onTab(visible[index - 1]);
          }
        }
      }
    };

    document.addEventListener('keydown', handler);

    return () => document.removeEventListener('keydown', handler);
  }, [onTab, onFilters, onExport, onSearch, onEscape, onHelp, hidden]);
};

export const SHORTCUT_HELP: { keys: string; does: string }[] = [
  { keys: '/', does: 'Focus the search box' },
  { keys: 'f', does: 'Show or hide the filters' },
  { keys: 'e', does: 'Export this view' },
  { keys: '1 – 5', does: 'Switch tab' },
  { keys: 'Esc', does: 'Clear the chart selection, or close the filters' },
];
